import { cn } from "@/lib/utils";
import { AlertTriangle } from "lucide-react";
import { toast, Toaster, ToasterProps } from "sonner";
import { ClassNameValue } from "tailwind-merge";
import { Button } from "./button";

type AlertType = "success" | "error" | "warning" | "info";

interface AlertOptions extends ToasterProps {
  description?: string;
  duration?: number;
  dismissTitle?: string;
}

const alertStyles: Record<
  AlertType,
  {
    icon: React.ReactNode;
    className: ClassNameValue;
    title: string;
    description: string;
  }
> = {
  success: {
    icon: <AlertTriangle size={24} className="text-uiColors-green-default" />,
    className: "bg-uiColors-white-default",
    title: "Success",
    description: "Operation completed successfully.",
  },
  error: {
    icon: <AlertTriangle size={24} className="text-uiColors-red-default" />,
    className: "bg-uiColors-red-lighter",
    title: "Error",
    description: "An error occurred. Please try again.",
  },
  warning: {
    icon: <AlertTriangle size={24} className="text-uiColors-orange-default" />,
    className: "bg-uiColors-yellow-lighter",
    title: "Warning",
    description: "Please proceed with caution.",
  },
  info: {
    icon: <AlertTriangle size={24} className="text-company-purple" />,
    className: "bg-uiColors-blue-lighter",
    title: "Information",
    description: "Here's some important information.",
  },
};

const showAlert = (type: AlertType, options: AlertOptions) => {
  const {
    dismissTitle,
    description,
    duration = 20000,
    position = "bottom-right",
    style,
  } = options;
  const { icon, className, title } = alertStyles[type];

  toast.custom(
    (t) => (
      <div
        className={cn(
          "flex items-center justify-between gap-4 rounded-md p-4 text-black",
          className
        )}
      >
        <div className="flex items-center gap-4">
          {icon}

          <span className="font-semibold">
            {title}: <span className="font-normal">{description}</span>
          </span>
        </div>
        <div className="flex items-end justify-end">
          <Button
            variant="secondary"
            title={dismissTitle || "Dismiss"}
            onClick={() => toast.dismiss(t)}
            className="h-fit rounded-lg border-[1px] border-uiColors-neutrals-blue-gray-light bg-white px-4 py-2 text-sm text-black"
          />
        </div>
      </div>
    ),
    { duration, position, style, classNames: { toast: "w-full" } }
  );
};

const defaultDuration = 2000;

export const CustomAlert = {
  success: (options: AlertOptions) =>
    showAlert("success", {
      ...options,
      duration: options.duration || defaultDuration,
    }),
  error: (options: AlertOptions) =>
    showAlert("error", {
      ...options,
      duration: options.duration || defaultDuration,
    }),
  warning: (options: AlertOptions) =>
    showAlert("warning", {
      ...options,
      duration: options.duration || defaultDuration,
    }),
  info: (options: AlertOptions) =>
    showAlert("info", {
      ...options,
      duration: options.duration || defaultDuration,
    }),
  Toaster,
};
